import { toast } from 'sonner'
import axios from '@/lib/axios'
import { paths } from '@/routes/paths'
import { useState, useEffect } from 'react'
import { fError } from '@/utils/format-error'
import { type Arena } from '@/types/arena.type'
import { useBoolean } from '@/hooks/use-boolean'
import { PaginateResponse } from '@/utils/paginate'
import { useRouter } from '@/routes/hooks/use-router'
import axiosInstance, { endpoints } from '@/lib/axios'
import { useDebounceText } from '@/hooks/use-debounce-text'
import CustomPagination from '@/components/pagination/custom-pagination'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LoadingSection from '@/components/loading-indicator/loading-section'

import ArenaTable from '../arena-table'
import ArenaDetailDialog from '../arena-detail-dialog'
import ArenaFilter, { type ArenaFilters } from '../arena-filter'

const PAGE_SIZE = 10

export default function ArenaView() {
  const router = useRouter()
  const loading = useBoolean(true)
  const [arenas, setArenas] = useState<Arena[]>([])
  const [pageMeta, setPageMeta] = useState({
    limit: PAGE_SIZE,
    page: 1,
    total: 0,
  })
  const [filters, setFilters] = useState<ArenaFilters>({
    page: 1,
    limit: PAGE_SIZE,
    search: '',
    orderBy: 'createdAt:desc',
  })

  // Debounced search value
  const debouncedSearch = useDebounceText(filters.search)

  // Detail dialog state
  const [detailDialog, setDetailDialog] = useState<{
    open: boolean
    arena: Arena | null
  }>({
    open: false,
    arena: null,
  })

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    arena: Arena | null
    action: 'delete' | null
  }>({
    open: false,
    arena: null,
    action: null,
  })

  // Loading state for operations
  const [actionLoading, setActionLoading] = useState(false)

  const fetchArenas = async () => {
    try {
      loading.onTrue()
      const { data } = await axios.get<PaginateResponse<Arena>>(
        endpoints.arenas.list,
        {
          params: {
            ...filters,
            search: debouncedSearch,
          },
        },
      )

      setArenas(data.items)
      setPageMeta({ limit: data.limit, page: data.page, total: data.total })
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      loading.onFalse()
    }
  }

  useEffect(() => {
    fetchArenas()
  }, [debouncedSearch, filters.page, filters.limit, filters.orderBy])

  const handleFiltersChange = (newFilters: Partial<ArenaFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
  }

  const handleAddClick = () => {
    router.push(paths.arenaCreate)
  }

  const handleEdit = (arena: Arena) => {
    router.push(paths.arenaEdit(arena.id))
  }

  const handleView = (arena: Arena) => {
    setDetailDialog({ open: true, arena })
  }

  const handleDelete = (arena: Arena) => {
    setConfirmDialog({ open: true, arena, action: 'delete' })
  }

  const handleConfirmAction = async () => {
    if (!confirmDialog.arena || !confirmDialog.action || actionLoading) return

    try {
      setActionLoading(true)

      switch (confirmDialog.action) {
        case 'delete':
          await axiosInstance.delete(
            endpoints.arenas.delete(confirmDialog.arena.id),
          )
          toast.success('ลบสนามแข่งขันสำเร็จ')
          break
      }

      // Refresh the list
      fetchArenas()
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      setActionLoading(false)
      setConfirmDialog({ open: false, arena: null, action: null })
    }
  }

  const handlePageChange = (page: number) => {
    setFilters({
      ...filters,
      page,
    })
  }

  const getConfirmDialogConfig = () => {
    if (!confirmDialog.arena || !confirmDialog.action) return null

    const configs = {
      delete: {
        title: 'ยืนยันการลบ',
        description: `คุณต้องการลบสนามแข่งขัน "${confirmDialog.arena.name}" หรือไม่? การดำเนินการนี้ไม่สามารถยกเลิกได้`,
        confirmText: 'ลบ',
        variant: 'destructive' as const,
      },
    }

    return configs[confirmDialog.action]
  }

  const dialogConfig = getConfirmDialogConfig()

  return (
    <div className="w-full space-y-6">
      {/* Filter Section */}
      <ArenaFilter
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onAddClick={handleAddClick}
      />

      <LoadingSection loading={loading.value}>
        <div className="space-y-6">
          {/* Table Section */}
          <ArenaTable
            arenas={arenas}
            loading={loading.value}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />

          {/* Pagination */}
          {Math.ceil(pageMeta.total / pageMeta.limit) > 1 && (
            <CustomPagination
              currentPage={filters.page}
              totalPages={Math.ceil(pageMeta.total / pageMeta.limit)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </LoadingSection>

      {/* Detail Dialog */}
      <ArenaDetailDialog
        open={detailDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            // Close dialog first
            setDetailDialog({ open: false, arena: detailDialog.arena })
            // Clear arena state after dialog closes
            setTimeout(() => {
              setDetailDialog({ open: false, arena: null })
            }, 150)
          } else {
            setDetailDialog({ open, arena: detailDialog.arena })
          }
        }}
        arena={detailDialog.arena}
      />

      {/* Confirm Dialog */}
      {dialogConfig && (
        <ConfirmDialog
          open={confirmDialog.open}
          onOpenChange={(open) =>
            setConfirmDialog({ open, arena: null, action: null })
          }
          title={dialogConfig.title}
          description={dialogConfig.description}
          confirmText={dialogConfig.confirmText}
          cancelText="ยกเลิก"
          variant={dialogConfig.variant}
          onConfirm={handleConfirmAction}
          loading={actionLoading}
        />
      )}
    </div>
  )
}
